import React from "react";
import useTruckCompatibilityViewModel, {
  formatCurrency,
  formatDelta,
  formatDurationMinutes,
  formatTonsWithUnit,
  getDeltaToneClass,
  getImpactToneClasses,
} from "../hooks/useTruckCompatibilityViewModel.js";
import { normalizeCurrencyDisplay } from "../utils/currencyDisplay.js";

function getTimeWindowStepLabel(stepType) {
  if (stepType === "loading") {
    return "Chargement";
  }

  if (stepType === "delivery") {
    return "Déchargement";
  }

  return "Étape";
}

function buildTimeWindowDiagnosticMeta(diagnostic) {
  const parts = [getTimeWindowStepLabel(diagnostic?.step_type)];
  const priority = typeof diagnostic?.priority === "string" ? diagnostic.priority.trim().toUpperCase() : "";
  if (priority) {
    parts.push(priority);
  }

  return parts.join(" • ");
}

function buildSnapshotSummary(snapshot) {
  if (!snapshot) {
    return [];
  }

  const compartmentsValue = Number.isInteger(snapshot?.total_compartments)
    ? `${snapshot?.compartments_used ?? 0}/${snapshot.total_compartments}`
    : null;

  return [
    snapshot?.capacity_tons != null ? `TM max ${formatTonsWithUnit(snapshot.capacity_tons)}` : null,
    snapshot?.current_load_tons != null ? `Charge ${formatTonsWithUnit(snapshot.current_load_tons)}` : null,
    snapshot?.remaining_capacity_tons != null ? `Reste ${formatTonsWithUnit(snapshot.remaining_capacity_tons)}` : null,
    `Cmd ${snapshot?.order_count ?? 0}`,
    `Trajets ${snapshot?.trip_count ?? 0}`,
    compartmentsValue ? `Comp. ${compartmentsValue}` : null,
  ].filter(Boolean);
}

function resolveSummaryMetrics({ summary, snapshot = null, emptyIfMissing = false }) {
  const hasSummary = Boolean(summary);
  const isEmptySnapshot = Boolean(
    snapshot
    && (snapshot?.trip_count ?? 0) === 0
    && (snapshot?.current_load_tons ?? 0) === 0,
  );
  const shouldUseEmptyFallback = !hasSummary && (emptyIfMissing || isEmptySnapshot);
  const durationValue = summary?.total_estimated_duration_label
    || (hasSummary && typeof summary?.total_estimated_duration_minutes === "number"
      ? formatDurationMinutes(summary.total_estimated_duration_minutes)
      : shouldUseEmptyFallback
        ? formatDurationMinutes(0)
        : "-");
  const costValue = normalizeCurrencyDisplay(summary?.total_estimated_cost_label)
    || (hasSummary && typeof summary?.total_estimated_cost === "number"
      ? formatCurrency(summary.total_estimated_cost)
      : shouldUseEmptyFallback
        ? formatCurrency(0)
        : "-");

  return {
    costValue,
    durationValue,
  };
}

function ComparisonSummaryRow({ summary, snapshot, title, emptyIfMissing = false }) {
  const metrics = resolveSummaryMetrics({ summary, snapshot, emptyIfMissing });

  return (
    <div className="grid grid-cols-[6.9rem_minmax(0,7.6rem)_minmax(0,7rem)] items-center gap-x-3 px-2.5 py-1.25">
      <div className="text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-muted-strong">
        {title}
      </div>
      <span className="tabular-nums whitespace-nowrap text-sm font-semibold text-text">
        {metrics.durationValue}
      </span>
      <span className="tabular-nums whitespace-nowrap text-sm font-semibold text-text">
        {metrics.costValue}
      </span>
    </div>
  );
}

function DeltaInline({ costDelta, durationDelta, label = "Impact" }) {
  const deltaCostLabel = formatDelta(costDelta, formatCurrency);
  const deltaDurationLabel = formatDelta(durationDelta, formatDurationMinutes);

  if (!deltaCostLabel && !deltaDurationLabel) {
    return null;
  }

  return (
    <div className="grid grid-cols-[6.9rem_minmax(0,7.6rem)_minmax(0,7rem)] items-center gap-x-3 px-2.5 py-1.25">
      <div className="text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-muted-strong">
        {label}
      </div>
      <span className={["tabular-nums whitespace-nowrap text-[0.78rem] font-semibold", getDeltaToneClass(durationDelta)].join(" ")}>
        {deltaDurationLabel || ""}
      </span>
      <span className={["tabular-nums whitespace-nowrap text-[0.78rem] font-semibold", getDeltaToneClass(costDelta)].join(" ")}>
        {deltaCostLabel || ""}
      </span>
    </div>
  );
}

function ComparisonPanel({
  afterLabel,
  afterSummary,
  afterEmptyIfMissing = false,
  beforeLabel,
  beforeSummary,
  costDelta,
  durationDelta,
  heading,
  snapshot,
  tone = "target",
}) {
  const toneClasses = getImpactToneClasses(tone);
  const surfaceClassName = tone === "target"
    ? "bg-[rgba(241,248,243,0.95)]"
    : tone === "source"
      ? "bg-[rgba(249,243,238,0.95)]"
      : "bg-[rgba(241,244,245,0.94)]";

  return (
    <div className={["rounded-lg border border-line-soft px-2.5 py-1.5 shadow-[0_6px_18px_rgba(15,23,42,0.03)]", surfaceClassName].join(" ")}>
      <div className={["text-[0.68rem] font-semibold uppercase tracking-[0.16em]", toneClasses.titleClassName].join(" ")}>
        {heading}
      </div>
      <div className="mt-1 grid gap-1">
        <div className="overflow-hidden rounded-md border border-line-soft bg-white/80">
          <ComparisonSummaryRow
            title={beforeLabel}
            summary={beforeSummary}
            snapshot={snapshot}
          />
          <div className="border-t border-line-soft" />
          <ComparisonSummaryRow
            title={afterLabel}
            summary={afterSummary}
            emptyIfMissing={afterEmptyIfMissing}
          />
          <div className="border-t border-line-soft" />
          <DeltaInline costDelta={costDelta} durationDelta={durationDelta} />
        </div>
      </div>
    </div>
  );
}

function FleetImpactCard({ routeImpact }) {
  const fleetCostLabel = formatDelta(routeImpact?.fleet_cost_delta_cad, formatCurrency) ?? "-";
  const fleetDurationLabel = formatDelta(routeImpact?.fleet_duration_delta_minutes, formatDurationMinutes) ?? "-";
  const toneClasses = getImpactToneClasses("fleet");

  return (
    <div className="rounded-xl border border-line-soft bg-[rgba(241,244,245,0.94)] px-2.5 py-1.5">
      <div className={["text-[0.68rem] font-semibold uppercase tracking-[0.16em]", toneClasses.titleClassName].join(" ")}>
        Flotte nette
      </div>
      <div className="mt-1 grid gap-1 sm:grid-cols-2 xl:grid-cols-1">
        <div className="rounded-lg border border-line-soft bg-white/80 px-2.5 py-1.25">
          <div className="text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-muted-strong">
            Coût global
          </div>
          <div className={["mt-0.5 text-sm font-semibold", getDeltaToneClass(routeImpact?.fleet_cost_delta_cad)].join(" ")}>
            {fleetCostLabel}
          </div>
        </div>
        <div className="rounded-lg border border-line-soft bg-white/80 px-2.5 py-1.25">
          <div className="text-[0.64rem] font-semibold uppercase tracking-[0.14em] text-muted-strong">
            Durée globale
          </div>
          <div className={["mt-0.5 text-sm font-semibold", getDeltaToneClass(routeImpact?.fleet_duration_delta_minutes)].join(" ")}>
            {fleetDurationLabel}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TruckCompatibilityItem({
  compatibility,
  currentAssignmentSummary,
  deliveryId,
  interactionsDisabled = false,
  onSelect,
  showRouteImpact = false,
}) {
  const viewModel = useTruckCompatibilityViewModel({ compatibility });
  const snapshotSummary = buildSnapshotSummary(viewModel.truckSnapshot);

  const handleClick = () => {
    if (!viewModel.isActionable || interactionsDisabled) {
      return;
    }

    onSelect?.(deliveryId, compatibility.truck_id, compatibility);
  };

  return (
    <li className="list-none" data-react-compatibility-item="true">
      <button
        className={[
          showRouteImpact
            ? "group flex w-full flex-col gap-1.5 rounded-2xl px-3.5 py-2 text-left transition duration-150 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
            : "group flex w-full flex-col gap-2.5 rounded-2xl px-3.5 py-3 text-left transition duration-150 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
          "bg-panel",
          viewModel.isActionable && !interactionsDisabled
            ? "hover:-translate-y-0.5 hover:bg-[rgba(245,248,247,0.98)] hover:shadow-[0_14px_30px_rgba(15,23,42,0.05)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            : "cursor-not-allowed opacity-95",
        ].join(" ")}
        type="button"
        data-react-compatibility-action="true"
        data-truck-id={compatibility?.truck_id ?? ""}
        disabled={!viewModel.isActionable || interactionsDisabled}
        onClick={handleClick}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <strong className="block truncate font-display text-base font-semibold text-text">
                {compatibility?.truck_name ?? "Camion"}
              </strong>
              {viewModel.stateLabel ? (
                <span
                  className={[
                    "rounded-full px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em]",
                    viewModel.stateToneClass,
                  ].join(" ")}
                >
                  {viewModel.stateLabel}
                </span>
              ) : null}
              {viewModel.requirementBadge ? (
                <span
                  className={[
                    "rounded-full px-2.5 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em]",
                    viewModel.requirementBadge.className,
                  ].join(" ")}
                >
                  {viewModel.requirementBadge.label}
                </span>
              ) : null}
              {snapshotSummary.length ? (
                <span className="min-w-0 flex-1 truncate text-[0.82rem] leading-5 text-muted-strong">
                  {snapshotSummary.join(" • ")}
                </span>
              ) : null}
            </div>
          </div>
          {viewModel.isActionable && !interactionsDisabled ? (
            <span
              className={[
                "rounded-full bg-[rgba(15,92,87,0.94)] px-3 py-1 text-sm font-semibold text-white transition-colors duration-150",
                "group-hover:bg-[#0c4b47]",
              ].join(" ")}
            >
              {viewModel.actionLabel}
            </span>
          ) : null}
        </div>

        {showRouteImpact && viewModel.hasRouteImpact ? (
          <div className="grid gap-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_14rem]">
            <ComparisonPanel
              heading="Camion cible"
              beforeLabel="Actuel"
              beforeSummary={viewModel.routeImpact?.target_before}
              afterLabel="Après ajout"
              afterSummary={viewModel.routeImpact?.target_after}
              costDelta={viewModel.routeImpact?.target_cost_delta_cad}
              durationDelta={viewModel.routeImpact?.target_duration_delta_minutes}
              snapshot={viewModel.truckSnapshot}
              tone="target"
            />
            <ComparisonPanel
              heading="Camion actuel"
              beforeLabel="Actuel"
              beforeSummary={viewModel.routeImpact?.source_before ?? currentAssignmentSummary}
              afterLabel="Après retrait"
              afterSummary={viewModel.routeImpact?.source_after}
              afterEmptyIfMissing
              costDelta={viewModel.routeImpact?.source_cost_delta_cad}
              durationDelta={viewModel.routeImpact?.source_duration_delta_minutes}
              tone="source"
            />
            <FleetImpactCard routeImpact={viewModel.routeImpact} />
          </div>
        ) : null}

        {viewModel.detailText ? (
          <div className="rounded-lg border border-line-soft bg-[rgba(244,246,247,0.96)] px-3 py-2 text-sm text-muted-strong">
            {viewModel.detailText}
          </div>
        ) : null}

        {viewModel.timeWindowDiagnostics.length ? (
          <div className="grid gap-1.5">
            {viewModel.timeWindowDiagnostics.map((diagnostic, index) => (
              <div
                key={`${compatibility?.truck_id || "truck"}-time-window-${diagnostic?.step_type || "step"}-${diagnostic?.delivery_id || diagnostic?.order_id || index}`}
                className="rounded-lg border border-[rgba(217,119,6,0.14)] bg-[rgba(255,251,235,0.9)] px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-[rgba(217,119,6,0.14)] bg-white px-2 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-[rgba(161,98,7,0.92)]">
                    {buildTimeWindowDiagnosticMeta(diagnostic)}
                  </span>
                  <span className="text-[0.74rem] font-semibold text-[rgba(146,64,14,0.94)]">
                    {normalizeCurrencyDisplay(diagnostic?.reason_label || compatibility?.reason_label || "Fenêtre horaire")}
                  </span>
                </div>
                <p className="mt-1 text-[0.8rem] leading-5 text-[rgba(120,53,15,0.94)]">
                  {normalizeCurrencyDisplay(diagnostic?.detail || compatibility?.detail || "Diagnostic indisponible.")}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </button>
    </li>
  );
}
